import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';

import { courtGridPublishConfirmDateFormat, dateTimeFormat, hasUnpublishedDrawsForMatches } from '../../../utils/courtSchedular';
import { publishScheduledMatches } from '../../../actions/court-scheduler-actions';
import { showInfoPopup } from '../../../actions/notification-actions';
import { UNPUBLISHED_DRAWS_SCHEDULE_WARNING } from '../../../../constants/events';
import styles from './courtSchedular.scss';

const PublishStatus = ({ event }) => {
    const dispatch = useDispatch();
    const {
        selectedDate,
        selectedDateCourtConfig,
        selectedFacilityId,
        isSaving = false,
        isSavingError = false,
        isPublishing = false,
        unPublishedMatchCount = 0,
        publishedDeletedDraftMatchCount = 0,
        matchesSaved = 0,
        lastPublishedDate,
        scheduledMatches,
        drawsList,
    } = useSelector(({ courtScheduler }) => courtScheduler);

    const dateOfConfig = moment(selectedDate).tz(event?.eventSchedule?.timeZone?.value).format(courtGridPublishConfirmDateFormat);

    const shouldDisplayPublishSection = selectedDateCourtConfig && selectedFacilityId;

    if (!shouldDisplayPublishSection) {
        return null;
    }

    const hasUnpublishedMatches = unPublishedMatchCount > 0 ||
        publishedDeletedDraftMatchCount > 0 ||
        matchesSaved?.length > 0;

    const onPublishClick = () => {
        const warningText = hasUnpublishedDrawsForMatches(scheduledMatches, drawsList)
            ? `<br><br>${UNPUBLISHED_DRAWS_SCHEDULE_WARNING}`
            : '';
        const message = `You are publishing updates for ${dateOfConfig} only${warningText}`;
        dispatch(showInfoPopup({
            reverseButtonOrder: true,
            noIcon: true,
            title: 'Publish Update',
            message,
            showCancelButton: true,
            cancelButtonLabel: 'Cancel',
            showConfirmButton: true,
            confirmButtonLabel: 'Publish',
            onConfirmClick: () => {
                dispatch(publishScheduledMatches());
            },
        }));
    };

    return (
        <>
            <div className={styles.publishSection} key="cs-saving" data-testid="court-scheduler.publish.save-status">
                <div
                    className={`d-flex ${styles.matchSaveStatus} ${isSavingError ? styles.error : ''} p0`}
                    type="button"
                >
                    <span
                        className={`${styles.savedBubble} ${isSaving ? styles.saving : ''} ${isSavingError ? styles.error : ''}`}
                    />
                    {isSavingError && 'Not saved'}
                    {!isSavingError && (isSaving ? 'Saving' : 'Saved')}
                </div>
                {lastPublishedDate && !isPublishing && (
                    <div className={styles.lastPublishedStatus}>
                        &nbsp;
                    </div>
                )}
            </div>
            {!hasUnpublishedMatches && (
                <div className={styles.publishSection} key="cs-published">
                    <div
                        className={`d-flex ${styles.matchSaveStatus} p0`}
                        type="button"
                    >
                        <span className={`${styles.savedBubble}`} />
                        <span data-testid="court-scheduler.publish-status.published-label">Published</span>
                    </div>
                    {lastPublishedDate && (
                        <div className={styles.lastPublishedStatus} data-testid="court-scheduler.publish.last-published">
                            {`Last Publish: ${moment.utc(lastPublishedDate).tz(event?.eventSchedule?.timeZone?.value).format(dateTimeFormat)}`}
                        </div>
                    )}
                </div>
            )}
            {hasUnpublishedMatches && isPublishing && (
                <div className={styles.publishSection} key="cs-publishing">
                    <div
                        className={`d-flex ${styles.matchSaveStatus} p0`}
                        type="button"
                    >
                        <span className={`${styles.loader}`} />
                        <span data-testid="court-scheduler.publish-status.publishing-label">Publishing...</span>
                    </div>
                </div>
            )}
            {hasUnpublishedMatches && !isPublishing && (
                <div className={styles.publishSection} key="cs-publish">
                    <button
                        className={`btn btn-primary ${styles.publish}`}
                        type="button"
                        onClick={onPublishClick}
                        data-testid="court-scheduler.publish-status.publish-btn"
                    >
                        {lastPublishedDate ? 'Publish updates' : 'Publish'}
                    </button>
                    {lastPublishedDate && (
                        <div className={styles.lastPublishedStatus} data-testid="court-scheduler.publish.last-published-updates">
                            {`Last Publish: ${moment.utc(lastPublishedDate).tz(event?.eventSchedule?.timeZone?.value).format(dateTimeFormat)}`}
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

PublishStatus.propTypes = {
    event: PropTypes.shape({
        eventSchedule: PropTypes.shape({
            timeZone: PropTypes.shape({
                value: PropTypes.string,
            }),
        }),
    }).isRequired,
};

export default PublishStatus;
